package co.maytes.standupbot.dashboard.styles

import co.maytes.standupbot.shared.design.DesignTokens
import co.maytes.standupbot.shared.design.ElevationTokens
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.design.StatusRole
import co.maytes.standupbot.shared.design.ThemeColor
import co.maytes.standupbot.shared.design.ThemeScheme
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.Color as ComposeColor
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px

internal val themeColors: List<ThemeColor> = DesignTokens.colors

private val themeColorsByName = themeColors.associateBy { it.cssName }

private fun tokenColor(cssName: String): CSSColorValue {
    val light = themeColorsByName.getValue(cssName).light
    return ComposeColor("var(--$cssName, $light)")
}

class StatusPalette internal constructor(hue: StatusHue) {
    val solid: CSSColorValue = tokenColor(DesignTokens.statusName(hue, StatusRole.Solid))
    val solidHover: CSSColorValue = tokenColor(DesignTokens.statusName(hue, StatusRole.SolidHover))
    val onSolid: CSSColorValue = tokenColor(DesignTokens.statusName(hue, StatusRole.OnSolid))
    val subtle: CSSColorValue = tokenColor(DesignTokens.statusName(hue, StatusRole.Subtle))
    val onSubtle: CSSColorValue = tokenColor(DesignTokens.statusName(hue, StatusRole.OnSubtle))
    val border: CSSColorValue = tokenColor(DesignTokens.statusName(hue, StatusRole.Border))
}

object Palette {
    val background: CSSColorValue = tokenColor("sb-bg")
    val surface: CSSColorValue = tokenColor("sb-surface")
    val surfaceSubtle: CSSColorValue = tokenColor("sb-surface-subtle")
    val surfaceHover: CSSColorValue = tokenColor("sb-surface-hover")
    val canvas: CSSColorValue = tokenColor("sb-canvas")
    val panel: CSSColorValue = tokenColor("sb-panel")
    val active: CSSColorValue = tokenColor("sb-active")
    val border: CSSColorValue = tokenColor("sb-border")
    val borderSubtle: CSSColorValue = tokenColor("sb-border-subtle")
    val textPrimary: CSSColorValue = tokenColor("sb-text-primary")
    val textSecondary: CSSColorValue = tokenColor("sb-text-secondary")
    val textTertiary: CSSColorValue = tokenColor("sb-text-tertiary")

    val primary: CSSColorValue = tokenColor("sb-primary")
    val primaryHover: CSSColorValue = tokenColor("sb-primary-hover")
    val onPrimary: CSSColorValue = tokenColor("sb-on-primary")

    val accent: CSSColorValue = tokenColor("sb-accent")
    val accentHover: CSSColorValue = tokenColor("sb-accent-hover")
    val accentSubtle: CSSColorValue = tokenColor("sb-accent-subtle")
    val accentBorder: CSSColorValue = tokenColor("sb-accent-border")
    val accentText: CSSColorValue = tokenColor("sb-accent-text")
    val onAccent: CSSColorValue = tokenColor("sb-on-accent")

    private val statusPalettes: Map<StatusHue, StatusPalette> =
        StatusHue.entries.associateWith { StatusPalette(it) }

    fun status(hue: StatusHue): StatusPalette = statusPalettes.getValue(hue)

    @Deprecated(
        "Use FocusRing, which draws the shared 2px accent outline on :focus-visible",
        ReplaceWith("FocusRing.OUTLINE", "co.maytes.standupbot.dashboard.styles.FocusRing"),
    )
    const val FOCUS_RING = "rgba(94, 106, 210, 0.22)"

    @Deprecated(
        "Use the danger, success and warning status roles",
        ReplaceWith("Palette.status(StatusHue.Success).solid", "co.maytes.standupbot.shared.design.StatusHue"),
    )
    val success: CSSColorValue get() = status(StatusHue.Success).solid

    @Deprecated(
        "Use the danger, success and warning status roles",
        ReplaceWith("Palette.status(StatusHue.Danger).solid", "co.maytes.standupbot.shared.design.StatusHue"),
    )
    val error: CSSColorValue get() = status(StatusHue.Danger).solid

    @Deprecated(
        "Use the danger, success and warning status roles",
        ReplaceWith("Palette.status(StatusHue.Warning).solid", "co.maytes.standupbot.shared.design.StatusHue"),
    )
    val warning: CSSColorValue get() = status(StatusHue.Warning).solid
}

object Spacing {
    val none: CSSSizeValue<CSSUnit.px> = 0.px
    val xxs = 0.25.cssRem
    val xs = 0.5.cssRem
    val sm = 0.75.cssRem
    val md = 1.cssRem
    val lg = 1.5.cssRem
    val xl = 2.cssRem
    val xxl = 3.cssRem
    val x3l = 4.cssRem
    val x4l = 6.cssRem
    val x5l = 8.cssRem
}

object Space {
    val s0: CSSSizeValue<CSSUnit.px> = 0.px
    val s0_5: CSSSizeValue<CSSUnit.px> = 2.px
    val s1: CSSSizeValue<CSSUnit.px> = 4.px
    val s2: CSSSizeValue<CSSUnit.px> = 8.px
    val s3: CSSSizeValue<CSSUnit.px> = 12.px
    val s4: CSSSizeValue<CSSUnit.px> = 16.px
    val s5: CSSSizeValue<CSSUnit.px> = 20.px
    val s6: CSSSizeValue<CSSUnit.px> = 24.px
    val s7: CSSSizeValue<CSSUnit.px> = 32.px
    val s8: CSSSizeValue<CSSUnit.px> = 40.px
    val s9: CSSSizeValue<CSSUnit.px> = 48.px
    val s10: CSSSizeValue<CSSUnit.px> = 64.px
    val s11: CSSSizeValue<CSSUnit.px> = 80.px
    val s12: CSSSizeValue<CSSUnit.px> = 96.px

    val entries: List<Pair<String, CSSSizeValue<CSSUnit.px>>> = listOf(
        "s0" to s0,
        "s0_5" to s0_5,
        "s1" to s1,
        "s2" to s2,
        "s3" to s3,
        "s4" to s4,
        "s5" to s5,
        "s6" to s6,
        "s7" to s7,
        "s8" to s8,
        "s9" to s9,
        "s10" to s10,
        "s11" to s11,
        "s12" to s12,
    )
}

object ControlHeight {
    val sm: CSSSizeValue<CSSUnit.px> = 28.px
    val md: CSSSizeValue<CSSUnit.px> = 32.px
    val lg: CSSSizeValue<CSSUnit.px> = 40.px

    val entries: List<Pair<String, CSSSizeValue<CSSUnit.px>>> = listOf("sm" to sm, "md" to md, "lg" to lg)
}

object IconSize {
    val sm: CSSSizeValue<CSSUnit.px> = 14.px
    val md: CSSSizeValue<CSSUnit.px> = 16.px
    val lg: CSSSizeValue<CSSUnit.px> = 20.px
}

object Radius {
    @Deprecated("Use Radius.inner", ReplaceWith("Radius.inner"))
    val sm = 4.px
    val md = 8.px
    val lg = 12.px
    val xl = 16.px

    @Deprecated("Use Radius.pill", ReplaceWith("Radius.pill"))
    val full = 9999.px

    val inner: CSSSizeValue<CSSUnit.px> = 4.px
    val chip: CSSSizeValue<CSSUnit.px> = 6.px
    val control: CSSSizeValue<CSSUnit.px> = 8.px
    val surface: CSSSizeValue<CSSUnit.px> = 12.px
    val pill: CSSSizeValue<CSSUnit.px> = 9999.px

    val roles: List<Pair<String, CSSSizeValue<CSSUnit.px>>> = listOf(
        "inner" to inner,
        "chip" to chip,
        "control" to control,
        "surface" to surface,
        "pill" to pill,
    )
}

object Shadow {
    const val XS = ElevationTokens.SHADOW_XS
    const val SM = ElevationTokens.SHADOW_SM
    const val MD = ElevationTokens.SHADOW_MD
    const val LG = ElevationTokens.SHADOW_LG
    const val XL = ElevationTokens.SHADOW_XL
}

object Elevation {
    const val MAX_LEVEL = ElevationTokens.MAX_LEVEL

    private val lightFallbacks = (0..MAX_LEVEL).map { ElevationTokens.spec(it, ThemeScheme.Light) }

    fun shadow(level: Int): String {
        val fallback = lightFallbacks[checkLevel(level)].shadow
        return "var(--${ElevationTokens.shadowName(level)}, $fallback)"
    }

    fun surface(level: Int): CSSColorValue {
        val fallback = lightFallbacks[checkLevel(level)].surface
        return ComposeColor("var(--${ElevationTokens.surfaceName(level)}, $fallback)")
    }

    private fun checkLevel(level: Int): Int {
        require(level in 0..MAX_LEVEL) { "elevation level must be within 0..$MAX_LEVEL but was $level" }
        return level
    }
}

fun Modifier.elevation(level: Int): Modifier = styleModifier {
    property("box-shadow", Elevation.shadow(level))
    property("background-color", Elevation.surface(level).toString())
}

object ZIndex {
    const val base = 0
    const val sticky = 100
    const val nav = 200
    const val dropdown = 1000
    const val overlay = 1100
    const val modal = 1200
    const val toast = 1300
    const val tooltip = 1400

    val entries: List<Pair<String, Int>> = listOf(
        "base" to base,
        "sticky" to sticky,
        "nav" to nav,
        "dropdown" to dropdown,
        "overlay" to overlay,
        "modal" to modal,
        "toast" to toast,
        "tooltip" to tooltip,
    )
}

object FontWeight {
    const val regular = 400
    const val medium = 500
    const val semibold = 600
    const val bold = 700

    val entries: List<Pair<String, Int>> = listOf(
        "regular" to regular,
        "medium" to medium,
        "semibold" to semibold,
        "bold" to bold,
    )
}

object Tracking {
    val tight: CSSSizeValue<CSSUnit.em> = (-0.01).em
    val normal: CSSSizeValue<CSSUnit.em> = 0.em
    val caps: CSSSizeValue<CSSUnit.em> = 0.06.em

    val entries: List<Pair<String, CSSSizeValue<CSSUnit.em>>> = listOf(
        "tight" to tight,
        "normal" to normal,
        "caps" to caps,
    )
}

object Layout {
    val reading: CSSSizeValue<CSSUnit.px> = 720.px
    val wide: CSSSizeValue<CSSUnit.px> = 1200.px
    val shell: CSSSizeValue<CSSUnit.px> = 1320.px
    val wizard: CSSSizeValue<CSSUnit.px> = 640.px
    val rowTextBasis: CSSSizeValue<CSSUnit.px> = 220.px

    val gutterMobile: CSSSizeValue<CSSUnit.px> = 16.px
    val gutterTablet: CSSSizeValue<CSSUnit.px> = 24.px
    val gutterDesktop: CSSSizeValue<CSSUnit.px> = 32.px

    const val breakpointSm = 640
    const val breakpointMd = 900
    const val breakpointLg = 1100
    const val breakpointXl = 1440

    val widths: List<Pair<String, CSSSizeValue<CSSUnit.px>>> = listOf(
        "reading" to reading,
        "wide" to wide,
        "shell" to shell,
    )

    val breakpoints: List<Pair<String, Int>> = listOf(
        "sm" to breakpointSm,
        "md" to breakpointMd,
        "lg" to breakpointLg,
        "xl" to breakpointXl,
    )
}

object FocusRing {
    const val WIDTH_PX = 2
    const val OFFSET_PX = 2
    const val OUTLINE = "${WIDTH_PX}px solid var(--sb-accent, #5E6AD2)"
    const val OFFSET = "${OFFSET_PX}px"
    const val VISIBLE_CLASS = "sb-focus-ring"
    const val ALWAYS_CLASS = "sb-focus-ring-always"
}

fun Modifier.focusRingOutline(): Modifier = styleModifier {
    property("outline", FocusRing.OUTLINE)
    property("outline-offset", FocusRing.OFFSET)
}

fun Modifier.focusRingOnFocusVisible(): Modifier = classNames(FocusRing.VISIBLE_CLASS)

fun Modifier.focusRingOnAnyFocus(): Modifier = classNames(FocusRing.ALWAYS_CLASS)

object Typography {
    const val TABULAR_CLASS = "sb-tabular-nums"

    val SANS_STACK = arrayOf(
        "Inter",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "sans-serif",
    )
    val SERIF_STACK = arrayOf(
        "Instrument Serif",
        "Iowan Old Style",
        "Palatino",
        "Georgia",
        "serif",
    )
    val MONO_STACK = arrayOf(
        "JetBrains Mono",
        "ui-monospace",
        "SFMono-Regular",
        "Menlo",
        "Monaco",
        "Consolas",
        "monospace",
    )
    const val FONT_SANS =
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    const val FONT_SITE =
        "'Geist', -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif"
    const val FONT_MONO =
        "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"

    data class TypeStyle(
        val size: CSSSizeValue<CSSUnit.px>,
        val lineHeight: Double,
        val weight: Int,
        val fontFamily: String = FONT_SANS,
    )

    val display = TypeStyle(48.px, 1.1, 700)
    val h1 = TypeStyle(32.px, 1.2, 700)
    val h2 = TypeStyle(24.px, 1.3, 600)
    val h3 = TypeStyle(20.px, 1.4, 600)
    val h4 = TypeStyle(18.px, 1.4, 600)
    val body = TypeStyle(15.px, 1.6, 400)
    val label = TypeStyle(14.px, 1.4, 500)
    val bodySmall = TypeStyle(13.px, 1.5, 400)
    val caption = TypeStyle(12.px, 1.5, 500)
    val code = TypeStyle(13.px, 1.6, 500, FONT_MONO)

    val displayLg = TypeStyle(56.px, 1.05, 500, FONT_SANS)
    val displayMd = TypeStyle(44.px, 1.05, 500, FONT_SANS)
    val displaySm = TypeStyle(32.px, 1.10, 500, FONT_SANS)
    val statValue = TypeStyle(42.px, 1.0, 500, FONT_SANS)
}

object Motion {
    const val EASE_STANDARD = "cubic-bezier(0.22, 0.61, 0.36, 1)"
    const val EASE_EMPHASIS = "cubic-bezier(0.16, 1, 0.3, 1)"
    const val EASE_DRAWER = "cubic-bezier(0.32, 0.72, 0, 1)"

    const val REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"
    const val FORCE_REDUCED_MOTION_CLASS = "sb-force-reduced-motion"

    const val DUR_FAST = "120ms"
    const val DUR_BASE = "200ms"

    const val PRESS_MS = 100
    const val RELEASE_MS = 160
    const val POPOVER_IN_MS = 150
    const val POPOVER_OUT_MS = 100
    const val MODAL_IN_MS = 200
    const val MODAL_OUT_MS = 140
    const val DRAWER_IN_MS = 280
    const val DRAWER_OUT_MS = 220
    const val SCRIM_MS = 200
    const val DISCLOSURE_MS = 200
    const val CONTENT_SWAP_MS = 160
    const val SEGMENT_THUMB_MS = 160
    const val CHEVRON_MS = 200
    const val CHECK_DRAW_MS = 240
    const val ICON_SWAP_MS = 150
    const val STATUS_IDLE_AFTER_MS = 1_500L
    const val COPIED_HINT_MS = 1_200L

    const val DUR_PRESS = "${PRESS_MS}ms"
    const val DUR_RELEASE = "${RELEASE_MS}ms"
    const val DUR_POPOVER_IN = "${POPOVER_IN_MS}ms"
    const val DUR_POPOVER_OUT = "${POPOVER_OUT_MS}ms"
    const val DUR_MODAL_IN = "${MODAL_IN_MS}ms"
    const val DUR_MODAL_OUT = "${MODAL_OUT_MS}ms"
    const val DUR_DRAWER_IN = "${DRAWER_IN_MS}ms"
    const val DUR_DRAWER_OUT = "${DRAWER_OUT_MS}ms"
    const val DUR_SCRIM = "${SCRIM_MS}ms"
    const val DUR_DISCLOSURE = "${DISCLOSURE_MS}ms"
    const val DUR_CONTENT_SWAP = "${CONTENT_SWAP_MS}ms"
    const val DUR_SEGMENT_THUMB = "${SEGMENT_THUMB_MS}ms"
    const val DUR_CHEVRON = "${CHEVRON_MS}ms"
    const val DUR_CHECK_DRAW = "${CHECK_DRAW_MS}ms"
    const val DUR_ICON_SWAP = "${ICON_SWAP_MS}ms"

    const val FIELD_CHECK_DRAW_MS = 140
    const val SWITCH_THUMB_MS = 160
    const val FIELD_ERROR_RISE_PX = 4
    const val SWITCH_SQUEEZE_PX = 2
    const val DUR_FIELD_CHECK_DRAW = "${FIELD_CHECK_DRAW_MS}ms"
    const val DUR_SWITCH_THUMB = "${SWITCH_THUMB_MS}ms"

    const val PRESS_SCALE = "0.97"
    const val BUTTON_PRESS_SCALE = "0.98"
    const val ICON_BUTTON_PRESS_SCALE = "0.96"
    const val PRESS_SCALE_VAR = "--sb-press-scale"
    const val POPOVER_SCALE = "0.96"
    const val MODAL_SCALE = "0.97"
    const val TOOLTIP_SCALE = "0.97"
    const val TOOLTIP_OFFSET_PX = 4
    const val ICON_SWAP_SCALE = "0.8"
    const val CHECK_DASH_LENGTH = 24

    const val OPEN_CLASS = "sb-open"
    const val PRESSABLE_CLASS = "sb-tap"

    const val TRANSITION_BASE =
        "background-color $DUR_BASE $EASE_STANDARD, " +
            "color $DUR_BASE $EASE_STANDARD, " +
            "border-color $DUR_BASE $EASE_STANDARD, " +
            "box-shadow $DUR_BASE $EASE_STANDARD, " +
            "opacity $DUR_BASE $EASE_STANDARD"
    const val TRANSITION_HOVER =
        "background-color $DUR_BASE $EASE_STANDARD, " +
            "color $DUR_BASE $EASE_STANDARD, " +
            "transform $DUR_FAST $EASE_STANDARD, " +
            "box-shadow $DUR_BASE $EASE_STANDARD, " +
            "border-color $DUR_BASE $EASE_STANDARD"

    fun transition(duration: String, vararg properties: String): String =
        properties.joinToString(", ") { "$it $duration $EASE_STANDARD" }

    fun transitionWith(duration: String, curve: String, vararg properties: String): String =
        properties.joinToString(", ") { "$it $duration $curve" }

    const val ENTER_DISTANCE_PX = 6
    const val ENTER_DUR = "260ms"
    const val CHART_INTRO_MS = 600
    const val CHART_UPDATE_MS = 300
    const val TOAST_IN_MS = 220
    const val TOAST_OUT_MS = 160
    const val TOAST_OFFSET_PX = 8
    const val STAGGER_STEP_MS = 40
    const val STAGGER_CAP_MS = 240
    const val ENTER_CLASS = "sb-enter-fade-up"

    const val PILL_MS = 280
    const val FLASH_MS = 1_400
    const val DUR_PILL = "${PILL_MS}ms"
    const val DUR_FLASH = "${FLASH_MS}ms"
}

object BrandHue {
    const val CHIP = "#FFFFFF"
    const val CHIP_RING = "rgba(15, 23, 42, 0.10)"
    const val LINEAR = "#5E6AD2"
    const val GITHUB = "#57606A"
    const val CLAUDE = "#D97757"
    const val SLACK = "#36C5F0"
    const val GOOGLE = "#4285F4"
}

object HomeArt {
    const val RING_SIZE = 132
    const val RING_STROKE = 10
    const val RING_RADIUS = 56
    const val RING_SETTLE_MS = 80L
    const val DUR_RING = "1000ms"
    const val DUR_BAR_GROW = "620ms"
    const val DUR_FLOW = "2200ms"
    const val DUR_WIRE_FADE = "320ms"
    const val WIRE_DASH_OFF = "4 5"
    const val WIRE_DASH_FLOW = "2 14"
    const val WIRE_FLOW_SHIFT = "-32"
    const val WIRE_BOX = "0 0 100 100"
    val WIRE_SOURCE_Y = listOf("9.09", "50", "90.91")
    const val WIRE_TARGET_Y = "50"
    const val WIRE_CURVE_X = "50"
    const val BAR_DAYS = 14
    const val AVATAR_STACK_MAX = 6
    const val AVATAR_PX = 36
    const val ICON_PX = 18
    const val STACK_GHOSTS = 4
    const val QUICK_ACTIONS_SHOWN = 4
    const val RUNS_SHOWN = 5
    const val DAY_COUNT = 7
    const val LIVE_LG_MIN = "(min-width: 1100px)"
    const val LIVE_MD_MIN = "(min-width: 700px)"
    const val HERO_SPLIT_MIN = "(min-width: 900px)"
    const val TINT_VAR = "--hm-tint"
    const val INDEX_VAR = "--hm-i"
    const val PROGRESS_VAR = "--hm-p"
    const val TINT_SOFT = "18%"
    const val TINT_FAINT = "6%"
    const val TINT_GLOW = "28%"
}

object Marketing {
    const val PAGE_CLASS = "mk-page"
    const val REVEAL_CLASS = "mk-rv"
    const val REVEAL_IN_CLASS = "mk-in"
    const val REVEAL_SCALE_CLASS = "mk-rv--scale"
    const val ENTER_CLASS = "mk-enter"
    const val INDEX_VAR = "--mk-i"
    const val REVEAL_THRESHOLD = 0.2
    const val STAGE_THRESHOLD = 0.3

    const val REVEAL_MS = 720
    const val REVEAL_STEP_MS = 80
    const val REVEAL_STEP_CAP = 5
    const val HERO_ENTER_MS = 820
    const val HERO_STEP_MS = 70
    const val FLOW_STEP_MS = 650
    const val LIFT_MS = 220
    const val RAIL_MS = 600

    const val DUR_REVEAL = "${REVEAL_MS}ms"
    const val DUR_HERO_ENTER = "${HERO_ENTER_MS}ms"
    const val DUR_LIFT = "${LIFT_MS}ms"
    const val DUR_RAIL = "${RAIL_MS}ms"
    const val DUR_NODE_IN = "560ms"
    const val DUR_PLATE_POP = "480ms"
    const val DUR_STAGE_FADE = "420ms"
    const val DUR_ACCENT_PULSE = "1400ms"

    const val REVEAL_DISTANCE = "1.5rem"
    const val ENTER_DISTANCE = "0.6em"
    const val REVEAL_SCALE_FROM = "0.96"
    const val LIFT_DISTANCE = "-3px"
    const val BUTTON_LIFT = "-1px"
    const val WINDOW_PENDING_SHIFT = "0.5rem"
    const val ACK_PRESS_SCALE = "0.94"
    const val ARROW_NUDGE = "3px"

    const val INTRO_BOTTOM_PAD = "clamp(2rem, 5vw, 4rem)"
    const val SECTION_PAD_TIGHT = "clamp(2.5rem, 6vw, 5rem)"
    const val NARROW_MEASURE = "56rem"
    const val FONT_SEATS = "clamp(1.75rem, 3vw, 2.25rem)"
    const val RAIL_INSET = "2.25rem"
    const val NODE_SIZE = "1.1rem"
    const val QNUM_SIZE = "2.5rem"
    const val FONT_H1 = "clamp(2.5rem, 6.4vw, 4.5rem)"
    const val FONT_PRICE = "clamp(2.75rem, 5vw, 3.75rem)"
    const val FONT_DISPLAY = "clamp(2.5rem, 7.2vw, 6rem)"
    const val FONT_H2 = "clamp(2rem, 4.8vw, 3.75rem)"
    const val FONT_LEAD = "clamp(1.0625rem, 1.5vw, 1.25rem)"
    const val FONT_CELL_LEAD = "clamp(1.625rem, 2.8vw, 2.25rem)"
    const val FONT_MARK = "clamp(3.5rem, 17vw, 16rem)"
    const val FONT_TRUST_LEAD = "clamp(1.5rem, 2.4vw, 2rem)"

    const val SECTION_PAD = "clamp(4rem, 10vw, 9rem)"
    const val HERO_TOP_PAD = "clamp(3rem, 7vw, 6rem)"
    const val GUTTER = "clamp(16px, 4vw, 32px)"
    const val BUTTON_HEIGHT = "3rem"
    const val BUTTON_HEIGHT_SM = "2.25rem"
    const val PLATE_SIZE = "3.5rem"
    const val STEP_DOT = "0.5rem"
    const val ANCHOR_OFFSET = "5rem"
    const val COPY_MEASURE = "38rem"
    const val HEADING_MEASURE = "22ch"
    const val TITLE_MEASURE = "20ch"
    const val SHOWCASE_MEASURE = "52rem"

    const val BRAND_LINEAR = "#5E6AD2"
    const val BRAND_GITHUB = "#8B949E"
    const val BRAND_CLAUDE = "#D97757"
    const val BRAND_SLACK = "#E01E5A"
    const val BRAND_MINT = "#2EB67D"

    const val CLOSE_BG = "#0C0C16"
    const val CLOSE_TEXT = "#F4F4F8"
    const val CLOSE_WHITE = "#FFFFFF"
    const val CLOSE_MUTED = "#B4B5C8"
    const val CLOSE_SURFACE = "rgba(255, 255, 255, 0.07)"
    const val CLOSE_EDGE = "rgba(255, 255, 255, 0.14)"
    const val CLOSE_SURFACE_HOVER = "rgba(255, 255, 255, 0.16)"

    const val DUR_DRIFT_A = "9s"
    const val DUR_DRIFT_B = "12s"
    const val DUR_DRIFT_C = "15s"
    const val DUR_ORB = "22s"
    const val DUR_SPIN = "36s"
    const val DUR_SPIN_FAST = "24s"
    const val DUR_TRAVEL = "2800ms"
    const val DUR_TRAVEL_OFFSET = "-1400ms"
    const val DUR_SHIMMER = "2600ms"
    const val DUR_PRESS_LOOP = "3600ms"
    const val DUR_RING_PULSE = "2800ms"
    const val DUR_POP = "480ms"
    const val DUR_BAR = "720ms"
    const val DUR_ITEM_IN = "560ms"
    const val ITEM_STEP_MS = 70
    const val ITEM_BASE_MS = 220
    const val DIAGRAM_STEP_MS = 240
    const val DELAY_DRIFT_B = "-3s"
    const val DELAY_DRIFT_C = "-6s"
    const val DELAY_DRIFT_D = "-9s"
    const val DELAY_ORB_B = "-7s"
    const val DELAY_ORB_C = "-14s"
    const val DUR_LEAD_IN = "620ms"
    const val CLOSE_ACCENT_GRADIENT = "linear-gradient(100deg, #AEB6FF, $BRAND_CLAUDE)"
    const val PULSE_X = "linear-gradient(90deg, transparent 38%, $BRAND_LINEAR 50%, transparent 62%)"
    const val PULSE_Y = "linear-gradient(180deg, transparent 38%, $BRAND_LINEAR 50%, transparent 62%)"
    const val PULSE_X_ALT = "linear-gradient(90deg, transparent 38%, $BRAND_CLAUDE 50%, transparent 62%)"
    const val PULSE_Y_ALT = "linear-gradient(180deg, transparent 38%, $BRAND_CLAUDE 50%, transparent 62%)"
    const val CLOSE_LAYERS =
        "radial-gradient(ellipse at 0% 0%, color-mix(in srgb, $BRAND_LINEAR 55%, transparent), transparent 55%), " +
            "radial-gradient(ellipse at 100% 100%, color-mix(in srgb, $BRAND_CLAUDE 38%, transparent), transparent 55%), " +
            CLOSE_BG
    const val CELL_TILT = "perspective(60rem) rotateX(1.6deg) rotateY(-1.6deg) translateY(-4px)"
    const val CHIP_TILT = "perspective(40rem) rotateX(3deg) rotateY(-3deg) translateY(-3px)"

    const val ORB_LARGE = "clamp(20rem, 56vw, 54rem)"
    const val ORB_MEDIUM = "clamp(15rem, 40vw, 36rem)"
    const val DOT_PITCH = "1.5rem 1.5rem"
    const val LINE_PITCH = "3rem 3rem"
    const val AURA_SIZE = "110%"
    const val LINK_THICK = "0.25rem"
    const val LINK_SPAN = "3.5rem"
    const val CLOSE_RING = "30rem"

    const val MASK_FADE = "radial-gradient(ellipse at center, black 25%, transparent 72%)"
    const val MASK_FADE_BOTTOM = "linear-gradient(to bottom, black 62%, transparent 100%)"
    const val MASK_RING = "radial-gradient(circle, transparent 56%, black 58%, black 68%, transparent 70%)"
    const val AURA_GRADIENT =
        "conic-gradient($BRAND_LINEAR, $BRAND_SLACK, $BRAND_CLAUDE, $BRAND_MINT, $BRAND_LINEAR)"
    const val ORB_CLAUDE = "radial-gradient(circle, color-mix(in srgb, $BRAND_CLAUDE 34%, transparent), transparent 68%)"
    const val ORB_SLACK = "radial-gradient(circle, color-mix(in srgb, $BRAND_SLACK 22%, transparent), transparent 68%)"
    const val ORB_MINT = "radial-gradient(circle, color-mix(in srgb, $BRAND_MINT 24%, transparent), transparent 68%)"
    const val DOTS_IMAGE = "radial-gradient(color-mix(in srgb, currentColor 26%, transparent) 1px, transparent 1px)"
    const val LINES_IMAGE =
        "linear-gradient(color-mix(in srgb, currentColor 9%, transparent) 1px, transparent 1px), " +
            "linear-gradient(90deg, color-mix(in srgb, currentColor 9%, transparent) 1px, transparent 1px)"

    const val TINT_SOFT = "color-mix(in srgb, var(--mk-tint) 14%, transparent)"
    const val TINT_EDGE = "color-mix(in srgb, var(--mk-tint) 38%, transparent)"
    const val TINT_BAR = "linear-gradient(180deg, color-mix(in srgb, var(--mk-tint) 78%, white), var(--mk-tint))"
    const val TINT_CHIP_SHADOW =
        "0 14px 36px -14px color-mix(in srgb, var(--mk-tint) 70%, transparent), " +
            "0 2px 6px -2px color-mix(in srgb, var(--mk-tint) 30%, transparent)"
    const val TINT_HOVER_GLOW =
        "radial-gradient(circle at 85% 0%, color-mix(in srgb, var(--mk-tint) 22%, transparent), transparent 62%)"
    const val SHADOW_CARD = "0 1px 0 color-mix(in srgb, currentColor 6%, transparent), " +
        "0 22px 44px -28px color-mix(in srgb, currentColor 45%, transparent)"
    const val SHADOW_LIFT = "0 28px 60px -26px color-mix(in srgb, currentColor 55%, transparent)"
    const val SHADOW_CLOSE = "0 30px 90px -34px $BRAND_LINEAR"
    const val SHADOW_BUTTON_GLOW = "0 14px 34px -12px $BRAND_LINEAR"
    const val BLUR_GLASS = "saturate(160%) blur(12px)"
    const val BLUR_SOFT = "blur(44px)"

    val GLASS = "color-mix(in srgb, ${Palette.surface} 82%, transparent)"
    val EDGE = "color-mix(in srgb, ${Palette.border} 80%, transparent)"
    val TEXT_GRADIENT = "linear-gradient(100deg, ${Palette.accent}, color-mix(in srgb, ${Palette.accent} 45%, $BRAND_CLAUDE))"
    val ORB_ACCENT = "radial-gradient(circle, color-mix(in srgb, ${Palette.accent} 40%, transparent), transparent 68%)"
    val ACCENT_BAR = "linear-gradient(90deg, ${Palette.accent}, color-mix(in srgb, ${Palette.accent} 40%, $BRAND_CLAUDE))"
    val STAGE_SHADOW =
        "0 40px 100px -40px color-mix(in srgb, ${Palette.accent} 65%, transparent), " +
            "0 0 0 1px color-mix(in srgb, ${Palette.accent} 16%, transparent)"
    val VAULT_SHADOW = "0 16px 40px -14px color-mix(in srgb, ${Palette.accent} 70%, transparent)"
}

enum class MarketingIcon(val slug: String) {
    Lock("lock"),
    Key("key"),
    Clock("clock"),
    Globe("globe"),
    Audit("audit"),
    Download("download"),
    Card("card"),
    Check("check"),
    Pencil("pencil"),
    Skip("skip"),
    History("history"),
    Shield("shield"),
    Calendar("calendar"),
    Users("users"),
    Bolt("bolt"),
    Building("building"),
    Mail("mail"),
    Lifebuoy("lifebuoy"),
    Chevron("chevron"),
    Book("book"),
}

object BrandChipColors {
    const val BACKGROUND = "#FFFFFF"
    const val BORDER = "rgba(9, 9, 11, 0.10)"
    const val INK = "#09090B"
}

object Wizard {
    const val NODE = "2rem"
    const val LINE = "2px"
    const val CHIP_SM = "2rem"
    const val CHIP_MD = "2.75rem"
    const val CHIP_LG = "3.5rem"
    const val ART_HEIGHT = "9.5rem"
    const val LINK_WIDTH = "3.5rem"
    const val LINK_WIDTH_PHONE = "2rem"
    const val DOT = "0.375rem"
    const val BADGE = "1.125rem"
    const val MARK = "1.25rem"
    const val SEAL = "2.25rem"
    const val SEAL_LARGE = "4rem"
    const val ICON_CHIP = "2.25rem"
    const val TICK = "1.125rem"
    const val SLIDE_DISTANCE = "0.75rem"
    const val TITLE_MEASURE = "34ch"
    const val SUB_MEASURE = "46ch"
    const val WASH_PERCENT = "14%"
    const val TINT_PERCENT = "8%"
    const val RING_PERCENT = "18%"
    const val RING_WIDTH = "4px"
    const val PHONE_MAX = Layout.breakpointSm - 1

    const val DUR_STEP_IN = "320ms"
    const val DUR_FILL = "640ms"
    const val DUR_POP = "380ms"
    const val DUR_FLOW = "1800ms"
    const val DUR_LIFT = "160ms"
    const val DUR_PULSE = "1200ms"
    const val HAIRLINE = "1px"

    const val LIFT = "-1px"
    const val PRESS_SCALE = "0.99"
    const val POP_FROM = "0.6"
    const val POP_PEAK = "1.12"

    fun tint(color: CSSColorValue, percent: String): String = "color-mix(in srgb, $color $percent, transparent)"
}

fun Modifier.tabularNums(): Modifier = classNames(Typography.TABULAR_CLASS)

object BrandChip {
    const val BACKGROUND = "#ffffff"
    const val BORDER = "rgba(15, 23, 42, 0.10)"
    const val DIMMED_FILTER = "grayscale(1)"
    const val DIMMED_OPACITY = "0.45"
}
